import React, { useState, useMemo } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography } from '@kis-technologies/kis-ui';
import { CreateVenuePayload } from 'store/venues';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps } from '../../types';
import { getVenueInitialForm, venueValidationSchema, getHasEmptyInput, getHasInvalidInput } from '../../utils';

export const CreateVenueForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getVenueInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateVenuePayload = {
      name: form.name,
      address: form.address
    };

    onSubmit(payload);
  };

  const onInputChange = (key: keyof ReturnType<typeof getVenueInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }
    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getVenueInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await venueValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getVenueInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  return (
    <FlexBox justify="center" flex="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Venue</Typography>
          </Box>

          <MuiBox className={classes.createClientName}>
            <InputField
              label="name"
              size="medium"
              fullWidth
              placeholder="name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
            />
          </MuiBox>
          <MuiBox className={classes.createClientName}>
            <InputField
              label="address"
              size="medium"
              fullWidth
              placeholder="address"
              value={form.address}
              onChange={(e) => onInputChange('address', e)}
              onBlur={(e) => onInputBlur('address', e, e.target.value.trim())}
              helperText={validate('address')}
              error={validation.address === INVALID}
            />
          </MuiBox>
        </Grid>
        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Venue
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
